// src/components/AdminProfile.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AdminProfile.css";
import { resolveProfilePhoto } from "../utils/profilePhoto";
import { clearStoredSession, redirectToLogin, updateStoredUser } from "../utils/sessionUser";
import { clearAdminPushSession } from "../utils/push";

/* ===== API endpoints (BACKEND must expose these) =====
   GET  /api/admin/profile/me
   PUT  /api/admin/profile/update         (multipart)
   PUT  /api/admin/profile/password       ({currentPassword,newPassword})
   POST /api/admin/profile/password/otp/send
   PUT  /api/admin/profile/password/otp/verify
*/
const API_GET_ME = "/api/admin/profile/me";
const API_UPDATE = "/api/admin/profile/update";
const API_CHANGE_PASSWORD = "/api/admin/profile/password";
const API_LOGOUT_ALL = "/api/admin/profile/logout-all";
const API_PW_OTP_SEND = "/api/admin/profile/password/otp/send";
const API_PW_OTP_VERIFY = "/api/admin/profile/password/otp/verify";

const pickPhotoValue = (data, fallback = "") => {
  const direct = data?.photo;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const alt = data?.profilePhoto || data?.avatar || data?.photoUrl;
  if (typeof alt === "string" && alt.trim()) return alt.trim();
  return fallback || "";
};

const AdminProfile = () => {
  const [profile, setProfile] = useState({ fullName: "", email: "", mobile: "", photo: "", photoVersion: 0 });
  const [initialProfile, setInitialProfile] = useState(null);
  const [initialOrgContext, setInitialOrgContext] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [imgFail, setImgFail] = useState(false);
  const [photoRetryCount, setPhotoRetryCount] = useState(0);
  const [prefs, setPrefs] = useState({
    emailNotifications: true,
    smsNotifications: false,
    whatsappNotifications: true,
    twoFactor: false,
    darkMode: false
  });
  const [orgContext, setOrgContext] = useState({
    companyId: "",
    department: "",
    joiningDate: "",
    accessLevel: ""
  });

  const permissions = useMemo(
    () => [
      "vendors:read",
      "vendors:approve",
      "employees:manage",
      "requirements:approve",
      "products:manage",
      "reports:view"
    ],
    []
  );

  const recentActions = useMemo(
    () => [
      { action: "Approved vendor requirement", by: "Admin", at: "Jan 29, 2026 4:12 PM" },
      { action: "Updated product pricing", by: "Admin", at: "Jan 28, 2026 11:05 AM" },
      { action: "Generated employee ID cards", by: "System", at: "Jan 28, 2026 12:00 AM" },
      { action: "Created new department role", by: "Admin", at: "Jan 27, 2026 6:45 PM" }
    ],
    []
  );

  // vendor-style preview logic
  const safePreview = useMemo(() => {
    if (photoFile instanceof File) return URL.createObjectURL(photoFile); // new file preview
    if (profile.photo && typeof profile.photo === "string") {
      return resolveProfilePhoto(profile.photo, profile.photoVersion);
    }
    return "";
  }, [photoFile, profile.photo, profile.photoVersion]);

  const [loading, setLoading] = useState(false);
  const [loadingMe, setLoadingMe] = useState(true);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);

  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", show: false });

  // OTP
  const [otpForm, setOtpForm] = useState({ email: "", otp: "", newPassword: "" });
  const [otpStep, setOtpStep] = useState(1);
  const [otpLoading, setOtpLoading] = useState(false);
  const resetForm = () => {
    if (!initialProfile) return;
    setProfile(initialProfile);
    setPhotoFile(null);
    setImgFail(false);
    setPhotoRetryCount(0);
    setPw({ currentPassword: "", newPassword: "", show: false });
    setOtpForm((f) => ({ ...f, email: initialProfile.email || "", otp: "", newPassword: "" }));
    setOtpStep(1);
    if (initialOrgContext) setOrgContext(initialOrgContext);
  };

  // axios client with auth
  const token = localStorage.getItem("token");
  const client = useMemo(() => {
    const c = axios.create();
    c.interceptors.request.use((config) => {
      config.headers = config.headers || {};
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    return c;
  }, [token]);

  // fetch profile
  useEffect(() => {
    let revokeUrl = null;

    const fetchProfile = async () => {
      try {
        setLoadingMe(true);
        const { data } = await client.get(API_GET_ME);
        const p = {
          fullName: data.fullName || "",
          email: data.email || "",
          mobile: data.mobile || "",
          photo: pickPhotoValue(data, ""),
          photoVersion: Date.now(),
        };
        setProfile(p);
        setInitialProfile(p);
        updateStoredUser({
          fullName: p.fullName,
          email: p.email,
          mobile: p.mobile,
          photo: p.photo,
          photoUpdatedAt: Date.now(),
        });
        const org = {
          companyId: data.adminOrgContext?.companyId || "",
          department: data.adminOrgContext?.departmentName || "",
          joiningDate: data.adminOrgContext?.joiningDate
            ? String(data.adminOrgContext.joiningDate).slice(0, 10)
            : "",
          accessLevel: data.adminOrgContext?.accessLevel || ""
        };
        setOrgContext(org);
        setInitialOrgContext(org);
        setOtpForm((f) => ({ ...f, email: p.email }));
        setImgFail(false);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load admin profile");
      } finally {
        setLoadingMe(false);
      }
    };

    fetchProfile();

    return () => {
      if (photoFile && typeof safePreview === "string" && safePreview.startsWith("blob:")) {
        revokeUrl = safePreview;
        URL.revokeObjectURL(revokeUrl);
      }
    };
  }, [client]); // client changes when token changes

  // dirty check
  const isDirty = useMemo(() => {
    if (!initialProfile) return false;
    const basicChanged = ["fullName", "email", "mobile"].some(
      (k) => String(profile[k] || "") !== String(initialProfile[k] || "")
    );
    const photoChanged = !!photoFile;
    const orgChanged = initialOrgContext
      ? ["companyId", "department", "joiningDate", "accessLevel"].some(
          (k) => String(orgContext[k] || "") !== String(initialOrgContext[k] || "")
        )
      : false;
    return basicChanged || photoChanged || orgChanged;
  }, [profile, initialProfile, photoFile, orgContext, initialOrgContext]);

  // pick image
  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(png|jpe?g|webp|gif|heic|heif)/i.test(f.type)) {
      toast.warn("Only image files are allowed");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.warn("Max file size 5MB");
      return;
    }
    setPhotoFile(f);
    setImgFail(false);
    setPhotoRetryCount(0);
  };

  // update profile
  const handleUpdate = async () => {
    if (!profile.fullName?.trim()) return toast.warn("Full name is required");
    if (!profile.email?.trim()) return toast.warn("Email is required");

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("fullName", profile.fullName);
      fd.append("email", profile.email);
      fd.append("mobile", profile.mobile || "");
      fd.append("companyId", orgContext.companyId || "");
      fd.append("departmentName", orgContext.department || "");
      fd.append("joiningDate", orgContext.joiningDate || "");
      fd.append("accessLevel", orgContext.accessLevel || "");
      if (photoFile instanceof File) fd.append("photo", photoFile);

      const { data } = await client.put(API_UPDATE, fd);

      const nextPhoto = pickPhotoValue(
        data,
        profile.photo || initialProfile?.photo || ""
      );
      const p = {
        fullName: data.fullName ?? profile.fullName,
        email: data.email ?? profile.email,
        mobile: data.mobile ?? profile.mobile,
        photo: nextPhoto,
        photoVersion: Date.now(),
      };
      setProfile(p);
      setInitialProfile(p);
      updateStoredUser({
        fullName: p.fullName,
        email: p.email,
        mobile: p.mobile,
        photo: p.photo,
        photoUpdatedAt: Date.now(),
      });
      const org = {
        companyId: data.adminOrgContext?.companyId || orgContext.companyId,
        department: data.adminOrgContext?.departmentName || orgContext.department,
        joiningDate: data.adminOrgContext?.joiningDate
          ? String(data.adminOrgContext.joiningDate).slice(0, 10)
          : orgContext.joiningDate,
        accessLevel: data.adminOrgContext?.accessLevel || orgContext.accessLevel
      };
      setOrgContext(org);
      setInitialOrgContext(org);
      setPhotoFile(null);
      setImgFail(false);
      setPhotoRetryCount(0);
      toast.success("✅ Profile updated successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  // change password
  const handlePasswordChange = async () => {
    if (!pw.currentPassword || !pw.newPassword) return toast.warn("Both fields required");
    if (pw.newPassword.length < 6) return toast.warn("Password must be at least 6 chars");
    setLoading(true);
    try {
      await client.put(API_CHANGE_PASSWORD, {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword
      });
      toast.success("🔒 Password changed successfully");
      setPw({ currentPassword: "", newPassword: "", show: false });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Password update failed");
    } finally {
      setLoading(false);
    }
  };

  // OTP send & verify
  const sendOtp = async () => {
    if (!otpForm.email?.trim()) return toast.warn("Enter email first");
    setOtpLoading(true);
    try {
      await client.post(API_PW_OTP_SEND, { email: otpForm.email });
      toast.success("OTP sent");
      setOtpStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpForm.otp?.trim() || !otpForm.newPassword?.trim())
      return toast.warn("Enter OTP and password");
    if (otpForm.newPassword.length < 6) return toast.warn("Password must be at least 6 chars");
    setOtpLoading(true);
    try {
      await client.put(API_PW_OTP_VERIFY, {
        email: otpForm.email,
        otp: otpForm.otp,
        newPassword: otpForm.newPassword
      });
      toast.success("Password reset successfully");
      setOtpForm({ email: profile.email, otp: "", newPassword: "" });
      setOtpStep(1);
    } catch (err) {
      toast.error(err?.response?.data?.message || "OTP verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLogoutEverywhere = async () => {
    const confirmed = window.confirm(
      "Is admin account ko sabhi devices aur browsers se logout karna hai?"
    );
    if (!confirmed) return;

    setLogoutAllLoading(true);
    try {
      await client.post(API_LOGOUT_ALL);
      toast.success("Logged out from all devices");
      await clearAdminPushSession().catch(() => {});
      clearStoredSession();
      setTimeout(redirectToLogin, 500);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Logout from all devices failed");
      setLogoutAllLoading(false);
    }
  };

  return (
    <div className="aprof-page">
      <div className="aprof-header">
        <div>
          <h1>Admin Profile</h1>
          <p>Manage your enterprise account settings, security, and preferences.</p>
        </div>
        <div className="aprof-header-actions">
          <button className="aprof-btn aprof-btn-ghost" onClick={resetForm}>
            Discard
          </button>
          <button
            className="aprof-btn aprof-btn-primary"
            onClick={handleUpdate}
            disabled={!isDirty || loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="aprof-grid">
        <div className="aprof-col">
          <section className="aprof-card">
            <div className="aprof-card-head">
              <h3>Profile Details</h3>
              {loadingMe ? <span className="aprof-tag">Loading...</span> : null}
            </div>
            <div className="aprof-card-body">
              <div className="aprof-profile-row">
                <div className="aprof-avatar">
                  {!imgFail && safePreview ? (
                    <img
                      src={safePreview}
                      alt="Admin"
                      onLoad={() => {
                        setImgFail(false);
                        setPhotoRetryCount(0);
                      }}
                      onError={() => {
                        if (!(photoFile instanceof File) && photoRetryCount < 1) {
                          setPhotoRetryCount((c) => c + 1);
                          setImgFail(false);
                          setProfile((p) => ({ ...p, photoVersion: Date.now() }));
                          return;
                        }
                        setImgFail(true);
                      }}
                    />
                  ) : (
                    <div className="aprof-avatar-fallback">
                      {profile.fullName?.[0]?.toUpperCase() || "A"}
                    </div>
                  )}
                </div>
                <div className="aprof-profile-meta">
                  <div className="aprof-name">{profile.fullName || "Admin"}</div>
                  <div className="aprof-role">Super Admin & System Architect</div>
                  <label className="aprof-upload">
                    <span className="material-symbols-outlined">upload</span>
                    Upload New Photo
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif"
                      onChange={onPickPhoto}
                    />
                  </label>
                  {photoFile ? (
                    <button
                      className="aprof-btn aprof-btn-ghost aprof-btn-small"
                      onClick={() => {
                        setPhotoFile(null);
                        setImgFail(false);
                        setPhotoRetryCount(0);
                      }}
                    >
                      Remove selection
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="aprof-form-grid">
                <label className="aprof-field">
                  <span>Full Name</span>
                  <input
                    type="text"
                    value={profile.fullName}
                    onChange={(e) => setProfile((p) => ({ ...p, fullName: e.target.value }))}
                  />
                </label>
                <label className="aprof-field">
                  <span>Email Address</span>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                  />
                </label>
              </div>

              <label className="aprof-field">
                <span>Mobile Number</span>
                <input
                  type="tel"
                  value={profile.mobile}
                  onChange={(e) => setProfile((p) => ({ ...p, mobile: e.target.value }))}
                />
              </label>
            </div>
          </section>

          <section className="aprof-card">
            <div className="aprof-card-head aprof-card-head-inline">
              <h3>Security & Credentials</h3>
              <span className="aprof-tag success">Strong</span>
            </div>
            <div className="aprof-card-body">
              <div className="aprof-form-grid">
                <label className="aprof-field">
                  <span>Current Password</span>
                  <input
                    type={pw.show ? "text" : "password"}
                    value={pw.currentPassword}
                    onChange={(e) => setPw((s) => ({ ...s, currentPassword: e.target.value }))}
                    placeholder="Enter current password"
                  />
                </label>
                <label className="aprof-field">
                  <span>New Password</span>
                  <input
                    type={pw.show ? "text" : "password"}
                    value={pw.newPassword}
                    onChange={(e) => setPw((s) => ({ ...s, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                  />
                </label>
              </div>

              <div className="aprof-inline">
                <label className="aprof-check">
                  <input
                    type="checkbox"
                    checked={pw.show}
                    onChange={(e) => setPw((s) => ({ ...s, show: e.target.checked }))}
                  />
                  <span>Show</span>
                </label>
                <button className="aprof-btn aprof-btn-primary" onClick={handlePasswordChange} disabled={loading}>
                  {loading ? "Updating..." : "Change Password"}
                </button>
              </div>

              <div className="aprof-otp">
                <div className="aprof-otp-title">Forgot Password? Reset with OTP</div>
                <div className="aprof-otp-row">
                  {otpStep === 1 ? (
                    <>
                      <input
                        className="aprof-otp-input"
                        type="email"
                        placeholder="Email address"
                        value={otpForm.email}
                        onChange={(e) => setOtpForm((f) => ({ ...f, email: e.target.value }))}
                      />
                      <button className="aprof-link" onClick={sendOtp} disabled={otpLoading}>
                        {otpLoading ? "Sending..." : "Send OTP"}
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        className="aprof-otp-code"
                        type="text"
                        placeholder="OTP"
                        value={otpForm.otp}
                        onChange={(e) => setOtpForm((f) => ({ ...f, otp: e.target.value }))}
                      />
                      <input
                        className="aprof-otp-input"
                        type="password"
                        placeholder="New Password"
                        value={otpForm.newPassword}
                        onChange={(e) => setOtpForm((f) => ({ ...f, newPassword: e.target.value }))}
                      />
                      <button className="aprof-link" onClick={verifyOtp} disabled={otpLoading}>
                        {otpLoading ? "Verifying..." : "Verify"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="aprof-danger-zone">
                <div>
                  <div className="aprof-danger-title">Logout From All Devices</div>
                  <div className="aprof-danger-sub">
                    Is admin ke sabhi active login sessions expire ho jayenge.
                  </div>
                </div>
                <button
                  className="aprof-btn aprof-btn-danger"
                  onClick={handleLogoutEverywhere}
                  disabled={logoutAllLoading}
                >
                  {logoutAllLoading ? "Logging out..." : "Logout All"}
                </button>
              </div>
            </div>
          </section>
        </div>

        <div className="aprof-col">
          <section className="aprof-card">
            <div className="aprof-card-head">
              <h3>Organization Context</h3>
            </div>
            <div className="aprof-card-body">
              <div className="aprof-form-grid">
                <label className="aprof-field">
                  <span>Company ID</span>
                  <input
                    type="text"
                    value={orgContext.companyId}
                    onChange={(e) => setOrgContext((o) => ({ ...o, companyId: e.target.value }))}
                    placeholder="ENT-9942-X"
                  />
                </label>
                <label className="aprof-field">
                  <span>Department</span>
                  <input
                    type="text"
                    value={orgContext.department}
                    onChange={(e) => setOrgContext((o) => ({ ...o, department: e.target.value }))}
                    placeholder="Cloud Infrastructure"
                  />
                </label>
              </div>
              <div className="aprof-form-grid">
                <label className="aprof-field">
                  <span>Joining Date</span>
                  <input
                    type="date"
                    value={orgContext.joiningDate}
                    onChange={(e) => setOrgContext((o) => ({ ...o, joiningDate: e.target.value }))}
                  />
                </label>
                <label className="aprof-field">
                  <span>Access Level</span>
                  <input
                    type="text"
                    value={orgContext.accessLevel}
                    onChange={(e) => setOrgContext((o) => ({ ...o, accessLevel: e.target.value }))}
                    placeholder="Tier 1 Global"
                  />
                </label>
              </div>
            </div>
          </section>

          <section className="aprof-card">
            <div className="aprof-card-head">
              <h3>Preferences</h3>
            </div>
            <div className="aprof-card-body">
              <div className="aprof-toggle-row">
                <div>
                  <div className="aprof-toggle-title">Email Notifications</div>
                  <div className="aprof-toggle-sub">Security alerts and system updates</div>
                </div>
                <label className="aprof-switch">
                  <input
                    type="checkbox"
                    checked={prefs.emailNotifications}
                    onChange={(e) => setPrefs((p) => ({ ...p, emailNotifications: e.target.checked }))}
                  />
                  <span />
                </label>
              </div>
              <div className="aprof-toggle-row">
                <div>
                  <div className="aprof-toggle-title">2FA Protection</div>
                  <div className="aprof-toggle-sub">Two-factor authentication on login</div>
                </div>
                <label className="aprof-switch">
                  <input
                    type="checkbox"
                    checked={prefs.twoFactor}
                    onChange={(e) => setPrefs((p) => ({ ...p, twoFactor: e.target.checked }))}
                  />
                  <span />
                </label>
              </div>
              <div className="aprof-toggle-row">
                <div>
                  <div className="aprof-toggle-title">WhatsApp Updates</div>
                  <div className="aprof-toggle-sub">Receive critical logs on mobile</div>
                </div>
                <label className="aprof-switch">
                  <input
                    type="checkbox"
                    checked={prefs.whatsappNotifications}
                    onChange={(e) => setPrefs((p) => ({ ...p, whatsappNotifications: e.target.checked }))}
                  />
                  <span />
                </label>
              </div>
              <div className="aprof-toggle-row">
                <div>
                  <div className="aprof-toggle-title">Dark Mode</div>
                  <div className="aprof-toggle-sub">Switch interface theme</div>
                </div>
                <label className="aprof-switch">
                  <input
                    type="checkbox"
                    checked={prefs.darkMode}
                    onChange={(e) => setPrefs((p) => ({ ...p, darkMode: e.target.checked }))}
                  />
                  <span />
                </label>
              </div>
            </div>
          </section>

          <section className="aprof-card">
            <div className="aprof-card-head">
              <h3>Assigned Permissions</h3>
            </div>
            <div className="aprof-card-body">
              <div className="aprof-badges">
                {permissions.map((p) => (
                  <span key={p} className="aprof-badge">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <section className="aprof-card">
            <div className="aprof-card-head">
              <h3>Recent Activity</h3>
            </div>
            <div className="aprof-card-body">
              <div className="aprof-activity-list">
                {recentActions.map((a, idx) => (
                  <div key={`${a.action}-${idx}`} className="aprof-activity">
                    <div className="aprof-activity-icon">
                      <span className="material-symbols-outlined">login</span>
                    </div>
                    <div>
                      <div className="aprof-activity-title">{a.action}</div>
                      <div className="aprof-activity-sub">{a.at}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button className="aprof-link-block">View Full Audit Log</button>
            </div>
          </section>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={2200} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover theme="colored" />
    </div>
  );
};

export default AdminProfile;
